import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchCurrencySelectOptions } from '#/api/system/currency';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import SideMsg from '#/modules/commonComponents/SideMsg';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import PurchaseOrderHubHeader from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderHubHeader';
import { lookupQueryKey } from '#/utils/queryKeys';

import {
  buildMiscPurchaseOrderColumns,
  miscPurchaseOrderSideMsgBlocks,
} from '../data/miscPurchaseOrderColumns';
import { getMiscPurchaseOrderApiErrorMessage } from '../helper/apiError';
import {
  applyMiscPurchaseOrderDateRangeToFilters,
} from '../helper/dateListFilter';
import useMiscPurchaseOrderList from '../hooks/useMiscPurchaseOrderList';

const SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  purchaseOrderNo: '採購單號',
  purchaseOrderDate: '採購日期',
};

const MiscPurchaseOrderListPage = () => {
  const canCreate = useCan(PERM.MISC_PURCHASE_ORDER_CREATE);
  const { displayData, setSelectedData } = useSideDetail();
  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setListQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useMiscPurchaseOrderList();

  const setDateRangeFilter = useCallback((range) => {
    const nextFilters = applyMiscPurchaseOrderDateRangeToFilters(
      listQuery.filters,
      range,
    );
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: nextFilters,
    }));
  }, [listQuery.filters, setListQuery]);

  const currencyOptionsQuery = useQuery({
    queryKey: lookupQueryKey('currencySelect'),
    queryFn: fetchCurrencySelectOptions,
  });
  const currencyOptions = currencyOptionsQuery.data;

  const columns = useMemo(
    () => buildMiscPurchaseOrderColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      onDateRangeChange: setDateRangeFilter,
      currencyOptions: currencyOptions ?? [],
    }),
    [listQuery.filters, setFilter, setDateRangeFilter, currencyOptions],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    setSelectedData(isSelected ? row.original : null);
  }, [setSelectedData]);

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <PurchaseOrderHubHeader page="nonStock" />
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              tableKey="srm.miscPurchaseOrder.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getMiscPurchaseOrderApiErrorMessage(error, '載入列表失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋採購單號、供應商、品名或備註"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              headerAction={
                canCreate
                  ? <ListAddButton to={ROUTES.miscPurchaseOrderCreate} />
                  : null
              }
            />
          </div>
          <SideMsg
            title="非庫存採購單"
            titleValue="supplierName"
            displayData={displayData}
            blocks={miscPurchaseOrderSideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default MiscPurchaseOrderListPage;
